/**
 * The chrome around one block: select it, drag it, duplicate it, delete it.
 *
 * Drawn as an overlay with `pointer-events` off, so the outline never covers
 * the letter and the letter's own spacing is never changed by being selected.
 * A builder that adds a border on hover shifts everything below it by two
 * pixels, and after an hour of that nobody trusts the preview.
 */
import { useState, type DragEvent, type ReactNode } from "react";
import { Copy, GripVertical, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { blockMeta, DRAG_MOVE } from "../catalog";
import { useBuilder } from "../store";
import type { Block } from "../types";

export function BlockFrame({ block, children }: { block: Block; children: ReactNode }) {
  const t = useT();
  const { selectedId, dispatch, disabled } = useBuilder();
  const [dragging, setDragging] = useState(false);
  const selected = selectedId === block.id;
  const meta = blockMeta(block.type);

  function onDragStart(e: DragEvent) {
    if (disabled) return;
    e.dataTransfer.setData(DRAG_MOVE, block.id);
    e.dataTransfer.effectAllowed = "move";
    setDragging(true);
  }

  return (
    <div
      data-block-id={block.id}
      className={cn("relative", dragging && "opacity-40")}
      onClick={(e) => {
        // A column's child and its parent both listen; the innermost wins.
        e.stopPropagation();
        dispatch({ kind: "select", id: block.id });
      }}
      onDragEnd={() => setDragging(false)}
    >
      {children}

      {/* The outline. Never in flow, so selecting never moves the letter. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute -inset-1 rounded-md ring-1 transition-opacity",
          selected ? "opacity-100 ring-primary" : "opacity-0 ring-primary/40 hover:opacity-100",
        )}
      />

      {/* The toolbar, shown for the selected block only. Placed above the block
          and pulled outside it, so it does not sit on top of the first line. */}
      {selected && !disabled && (
        <div
          className="absolute -top-3.5 right-0 z-10 flex items-center gap-0.5 rounded-md border border-border bg-card px-1 py-0.5 shadow-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            draggable
            onDragStart={onDragStart}
            title={t("marketing.builder.drag")}
            className="cursor-grab rounded p-1 text-muted-foreground hover:bg-accent active:cursor-grabbing"
          >
            <GripVertical className="size-3.5" />
          </span>
          <span className="px-1 text-[10px] font-medium text-muted-foreground">
            {t(`marketing.builder.block.${meta.key}`)}
          </span>
          <button
            type="button"
            title={t("marketing.builder.duplicate")}
            onClick={() => dispatch({ kind: "duplicate", id: block.id })}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Copy className="size-3.5" />
          </button>
          <button
            type="button"
            title={t("marketing.builder.remove")}
            onClick={() => dispatch({ kind: "remove", id: block.id })}
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
